import { useState, useCallback, type FormEvent } from "react";
import { Send, CheckCircle2, Mail } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { sendSupportMessage, SupportError } from "@/lib/support";
import { TurnstileWidget, isTurnstileConfigured } from "@/components/TurnstileWidget";

export function SupportPage() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(user?.email ?? "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  /** Honeypot — visually hidden field. Real users leave it empty. */
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");

  const handleVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (isTurnstileConfigured && !turnstileToken) {
      setError("Please complete the verification check below before sending.");
      return;
    }

    setSubmitting(true);
    try {
      await sendSupportMessage({
        name: name.trim() || undefined,
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        _hp: honeypot,
        turnstile_token: turnstileToken || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof SupportError
          ? err.message
          : "Something went wrong. Please email us directly at support@doclens.app.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 md:py-24 text-center space-y-4">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
        <h1 className="text-2xl md:text-3xl font-bold">Thanks — we got your message</h1>
        <p className="text-neutral-600">
          We'll reply to{" "}
          <span className="font-medium text-neutral-900">{email}</span> within one business day.
        </p>
        <p className="text-sm text-neutral-500">
          Don't see a response? Check your spam folder, or email us directly at{" "}
          <a href="mailto:support@doclens.app" className="underline">
            support@doclens.app
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-12 md:py-16 space-y-8">
      <header className="space-y-2">
        <h1 className="text-3xl md:text-4xl font-bold">Help & support</h1>
        <p className="text-neutral-600">
          Question about pricing, an upload error, an API integration, or a feature request? Send
          us a note and we'll get back to you.
        </p>
      </header>

      <div className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">
        <Mail className="h-5 w-5 text-neutral-500 flex-shrink-0 mt-0.5" />
        <div>
          Prefer email? Reach us directly at{" "}
          <a href="mailto:support@doclens.app" className="font-medium text-neutral-900 underline">
            support@doclens.app
          </a>
          .
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="support-name" className="block text-sm font-medium mb-1">
              Name <span className="text-neutral-400 font-normal">(optional)</span>
            </label>
            <input
              id="support-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
              autoComplete="name"
            />
          </div>
          <div>
            <label htmlFor="support-email" className="block text-sm font-medium mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="support-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={200}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
              autoComplete="email"
            />
          </div>
        </div>

        <div>
          <label htmlFor="support-subject" className="block text-sm font-medium mb-1">
            Subject <span className="text-red-500">*</span>
          </label>
          <input
            id="support-subject"
            type="text"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            placeholder="What's this about?"
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
          />
        </div>

        <div>
          <label htmlFor="support-message" className="block text-sm font-medium mb-1">
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="support-message"
            required
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            rows={6}
            placeholder="Tell us what's going on. The more detail you can include, the faster we can help."
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand resize-y"
          />
          <div className="text-xs text-neutral-400 mt-1 text-right tabular-nums">
            {message.length} / 5000
          </div>
        </div>

        {/* Honeypot — visually hidden but in the DOM. Real users won't fill this. */}
        <div aria-hidden className="absolute -left-[9999px] -top-[9999px] h-0 w-0 overflow-hidden">
          <label htmlFor="support-hp">Leave this field empty</label>
          <input
            id="support-hp"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        {isTurnstileConfigured && (
          <div className="pt-1">
            <TurnstileWidget onVerify={handleVerify} onExpire={() => setTurnstileToken("")} />
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || (isTurnstileConfigured && !turnstileToken)}
          className="inline-flex items-center gap-2 rounded-lg bg-brand text-white px-5 py-2.5 text-sm font-medium hover:bg-brand-700 transition disabled:opacity-60"
        >
          {submitting ? "Sending…" : "Send message"}
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
